// Client-visible URL namespace helpers (Phase 0 of the owner-bypass project).
//
// BrowserDAV is addressable by two route segments — the stable `serverName`
// and the ephemeral `instanceId` (sessionStorage-scoped, regenerated each session).
// Both stay registered as routes, but anything the server *emits* — PROPFIND
// hrefs, the file browser's root-url, editor file URLs — must use the stable
// form, or the URL goes dead as soon as the serving tab reloads.
//
// Nephele resolves getCanonicalUrl() against request.baseUrl, i.e. it derives
// emitted URLs from how the request arrived rather than from server state, and
// emits them absolute. These helpers are the BrowserDAV equivalent: behind the
// /groups/ peer proxy the arrival namespace is carried by X-Forwarded-Proto /
// -Host / -Prefix (always set by localDiscovery's peerProxy, never accepted
// from the client).
//
// Kept dependency-free so it is unit-testable outside the browser app, the way
// endpoints.js is.

/**
 * Absolute href base for a request, ending in '/'.
 *
 * Returns null when the request carries no usable forwarding headers — which
 * is the normal case for the direct-peer and service-worker transports, where
 * there is no HTTP proxy in front. Callers fall back to their instance-derived
 * base.
 *
 * @param {object} req - { headers, path } from the proxied request
 * @returns {string|null} e.g. 'https://acequia.io/groups/browserdav/jphone/'
 */
export function forwardedHrefBase({ headers = {}, path = '' } = {}) {
    const prefix = headers['x-forwarded-prefix']
    const host = headers['x-forwarded-host']
    const proto = headers['x-forwarded-proto'] || 'https'

    if (typeof prefix !== 'string' || typeof host !== 'string') return null
    if (!prefix.startsWith('/')) return null
    if (proto !== 'http' && proto !== 'https') return null
    if (UNSAFE.test(prefix) || UNSAFE.test(host)) return null
    if (prefix.split('/').some((seg) => seg === '.' || seg === '..')) return null

    // The route segment the client actually addressed (serverName or
    // instanceId). Taken from the wire, so it is already in URL form — do not
    // re-encode it or legitimately-encoded names get double-escaped.
    const segment = String(path || '')
        .split('/')
        .filter(Boolean)[0]
    if (!segment || UNSAFE.test(segment) || segment === '.' || segment === '..') return null

    return `${proto}://${host}${prefix.replace(/\/+$/, '')}/${segment}/`
}

const UNSAFE = /[\s<>"'\\]/

/**
 * Absolute base for this instance built from local state, ending in '/'.
 * Used when the request carries no forwarding headers.
 *
 * @param {object} opts - { discoveryOrigin, groupName, segment }
 * @returns {string}
 */
export function instanceRootUrl({ discoveryOrigin, groupName, segment }) {
    return `${discoveryOrigin}/groups/${encodeURIComponent(groupName)}/${encodeURIComponent(segment)}/`
}